import React, { useState, useMemo, useCallback } from 'react';
import { Stack, Row, Col, Spinner, Form } from 'react-bootstrap';
import Button from '../../../../../../components/button';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import { StepIndicator } from './components/StepIndicator';
import { FieldInput } from '../../../../components/EmployeeData';
import { FormatFieldValue } from '../components/FormatFieldValue';
import { RecordField } from './types';
import { useToasts } from '../../../../../../context/Toast';
import { Form as CustomForm } from '../../../../../../components/form/Form';
import { SelectSingle } from '../../../../components/Selects/SelectSingle';
import { RequestFormFragment } from '../../../../__generated__/graphql';
import UploadFile from '../../../../../../components/UploadFile';
import { Attachments } from './components/Attachments';
import {
  SalaryChangeStepsWrapper,
  SalaryChangeStepsWrapperRef,
  SalaryChangeData,
} from './components/salaryChange';
import {
  PositionChangeComponent,
  PositionChangeData,
} from './components/positionChange';
import { validateDateRange } from './components/utils/dateValidation';

export type StepTwoData = {
  employeeId: string;
  reason: string;
  fields: {
    [key: string]: string;
  };
  files: File[];
  salaryChange?: SalaryChangeData;
  positionChange?: PositionChangeData;
};

type StepTwoProps = {
  onSubmit: (data: StepTwoData) => void;
  onBack: () => void;
  initialData?: StepTwoData;
  employees: ReadonlyArray<{
    Id: string;
    Name: string;
  }>;
  selectedRequestForm?: RequestFormFragment | null;
  recordFields: Array<RecordField>;
  recordFieldsLoading: boolean;
  onEmployeeSelect: (employeeId: string | null) => void;
  onSubStepChange?: (subStep: number) => void;
  lastSubStep?: number;
};

type NewFieldValues = {
  [key: string]: string;
};

const StepType = {
  REASON_FILES: 'reason_files',
  POSITION_CHANGE: 'position_change',
  SALARY_CHANGE: 'salary_change',
  EMPLOYEE_DETAILS: 'employee_details',
} as const;

type StepInfo = {
  type: (typeof StepType)[keyof typeof StepType];
  title: string;
  required: boolean;
};

export const StepTwo: React.FC<StepTwoProps> = ({
  onSubmit,
  onBack,
  initialData,
  employees,
  selectedRequestForm,
  recordFields,
  recordFieldsLoading,
  onEmployeeSelect,
  onSubStepChange,
  lastSubStep = 0,
}) => {
  const t = useNamespacedTranslation('changeRequests.requestForm');
  const { addError } = useToasts();

  // Employee selection state
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    initialData?.employeeId || null,
  );

  // Track previous selectedEmployeeId to detect transitions
  const prevSelectedEmployeeId = React.useRef<string | null>(
    selectedEmployeeId,
  );

  // Clear employee name when transitioning to employee selection page
  React.useEffect(() => {
    if (
      prevSelectedEmployeeId.current !== null &&
      selectedEmployeeId === null
    ) {
      onEmployeeSelect(null);
    }
    prevSelectedEmployeeId.current = selectedEmployeeId;
  }, [selectedEmployeeId, onEmployeeSelect]);

  // Step data states
  const [reason, setReason] = useState(initialData?.reason || '');
  const [selectedFiles, setSelectedFiles] = useState<File[]>(
    initialData?.files || [],
  );
  const [salaryChangeData, setSalaryChangeData] = useState<
    SalaryChangeData | undefined
  >(initialData?.salaryChange);
  const [positionChangeData, setPositionChangeData] = useState<
    PositionChangeData | undefined
  >(initialData?.positionChange);
  const [employeeDetailsData, setEmployeeDetailsData] = useState<{
    [key: string]: string;
  }>(initialData?.fields || {});

  const salaryChangeRef = React.useRef<SalaryChangeStepsWrapperRef>(null);

  const availableSteps = useMemo<StepInfo[]>(() => {
    const steps: StepInfo[] = [];

    steps.push({
      type: StepType.REASON_FILES,
      title: t('steps.reasonFiles'),
      required: true,
    });

    // Step 2: Position Change - Optional (if Position_Change_Enabled__c is true)
    const positionChangeEnabled =
      selectedRequestForm?.flair__Position_Change_Enabled__c === true;
    if (positionChangeEnabled) {
      steps.push({
        type: StepType.POSITION_CHANGE,
        title: t('steps.positionChange'),
        required: false,
      });
    }

    // Step 3: Salary Change - Optional (if Salary_Change_Enabled__c is true)
    const salaryChangeEnabled =
      selectedRequestForm?.flair__Salary_Change_Enabled__c === true;
    if (salaryChangeEnabled) {
      steps.push({
        type: StepType.SALARY_CHANGE,
        title: t('steps.salaryChange'),
        required: false,
      });
    }

    // Step 4: Employee Details Change - Optional (if Data_Change_Objects_Fields__c contains at least one field on flair__Employee__c)
    const hasEmployeeFields = recordFields.length > 0;

    if (hasEmployeeFields) {
      steps.push({
        type: StepType.EMPLOYEE_DETAILS,
        title: t('steps.employeeDetails'),
        required: false,
      });
    }

    return steps;
  }, [selectedRequestForm, recordFields, t]);

  // Current sub-step state
  const [currentSubStep, setCurrentSubStep] = useState(lastSubStep);
  const currentStep = availableSteps[currentSubStep];

  // Notify parent when sub-step changes
  React.useEffect(() => {
    if (onSubStepChange) {
      onSubStepChange(currentSubStep);
    }
  }, [currentSubStep, onSubStepChange]);

  const isReasonMandatory =
    selectedRequestForm?.flair__Mandatory_Reason__c || false;

  // Get current salary from recordFields
  const currentSalary = useMemo(() => {
    const salaryField = recordFields.find(
      (field) =>
        field.fieldInfo.name.toLowerCase().includes('salary') ||
        field.fieldInfo.name.toLowerCase().includes('amount'),
    );
    return salaryField ? parseFloat(salaryField.value || '1000') : 1000;
  }, [recordFields]);

  // Create default values for the dynamic fields
  const defaultValues = useMemo(() => {
    const fieldValues: { [key: string]: string } = {};
    recordFields.forEach((field) => {
      fieldValues[field.fieldInfo.name] =
        initialData?.fields?.[field.fieldInfo.name] || field.value || '';
    });
    return fieldValues;
  }, [recordFields, initialData]);

  const handleFinalSubmit = useCallback(() => {
    if (!selectedEmployeeId) {
      addError(t('errors.selectEmployee'));
      return;
    }
    const hasEmployeeDetailsStep = availableSteps.some(
      (step) => step.type === StepType.EMPLOYEE_DETAILS,
    );
    const hasValidSalaryChange =
      salaryChangeData &&
      salaryChangeData.formData?.value &&
      !salaryChangeData.hasPermissionIssue;
    const hasValidPositionChange =
      positionChangeData && positionChangeData.newPositionId;
    const hasEmployeeDetailsChanges =
      Object.keys(employeeDetailsData).length > 0;

    if (
      hasEmployeeDetailsStep &&
      !hasEmployeeDetailsChanges &&
      !hasValidPositionChange &&
      !hasValidSalaryChange
    ) {
      if (salaryChangeData?.hasPermissionIssue) {
        addError(t('errors.noChangesWithPermissionIssue'));
      } else {
        addError(t('errors.noChanges'));
      }
      return;
    }
    onSubmit({
      employeeId: selectedEmployeeId,
      reason,
      fields: employeeDetailsData,
      files: selectedFiles,
      salaryChange: salaryChangeData,
      positionChange: positionChangeData,
    });
  }, [
    selectedEmployeeId,
    reason,
    selectedFiles,
    salaryChangeData,
    positionChangeData,
    employeeDetailsData,
    availableSteps,
    addError,
    t,
    onSubmit,
  ]);

  // Navigation handlers
  const handleSubStepNext = useCallback(() => {
    if (currentSubStep < availableSteps.length - 1) {
      setCurrentSubStep(currentSubStep + 1);
    } else {
      // Final step - submit all data
      handleFinalSubmit();
    }
  }, [currentSubStep, availableSteps.length, handleFinalSubmit]);

  const handleSubStepBack = useCallback(() => {
    if (
      currentStep?.type === StepType.SALARY_CHANGE &&
      salaryChangeRef.current
    ) {
      const handled = salaryChangeRef.current.goBack();
      if (handled) {
        return;
      }
    }

    if (currentSubStep > 0) {
      setCurrentSubStep(currentSubStep - 1);
    } else {
      setSelectedEmployeeId(null);
    }
  }, [currentSubStep, currentStep?.type]);

  // Validation for current step
  const isCurrentStepValid = useMemo(() => {
    if (!currentStep) return false;

    switch (currentStep.type) {
      case StepType.REASON_FILES:
        return !isReasonMandatory || reason.trim().length > 0;
      case StepType.POSITION_CHANGE:
        if (!positionChangeData) return false;

        // Must select a different position than current
        const hasSelectedNewPosition =
          positionChangeData.newPositionId &&
          positionChangeData.newPositionId !==
            positionChangeData.currentPositionId;

        // Must have effective date
        const hasEffectiveDate =
          positionChangeData.effectiveDate != null &&
          positionChangeData.effectiveDate !== '';

        // If end date is not unknown, it must be provided
        const hasValidEndDate =
          positionChangeData.unknownEndDate === true ||
          (positionChangeData.endDate != null &&
            positionChangeData.endDate !== '');

        // Check for date validation errors
        const dateValidationErrors = validateDateRange(
          positionChangeData.effectiveDate || '',
          positionChangeData.unknownEndDate
            ? undefined
            : positionChangeData.endDate || '',
        );
        const hasNoDateErrors =
          !dateValidationErrors.effectiveDateError &&
          !dateValidationErrors.endDateError;

        return (
          hasSelectedNewPosition &&
          hasEffectiveDate &&
          hasValidEndDate &&
          hasNoDateErrors
        );
      case StepType.SALARY_CHANGE:
        if (!salaryChangeData) {
          return false;
        }

        // If permission issue, allow to proceed (no salary data will be sent)
        if (salaryChangeData.hasPermissionIssue) {
          return true;
        }

        // If user has permission, then either provide complete salary data OR skip entirely
        // Allow proceeding if no salary change data is entered (optional step)
        const hasAnyData =
          salaryChangeData.formData?.value ||
          salaryChangeData.formData?.effectiveDate ||
          salaryChangeData.formData?.selectedSalaryId;
        if (!hasAnyData) {
          return true;
        }

        // If they started entering data, validate it's complete
        const hasValue =
          salaryChangeData.formData?.value &&
          parseFloat(salaryChangeData.formData.value) !== 0;

        // Check dates - consider synced dates from position change
        const syncDatesWithPosition =
          salaryChangeData.formData?.syncDatesWithPosition;
        const effectiveStartDate =
          syncDatesWithPosition && positionChangeData?.effectiveDate
            ? positionChangeData.effectiveDate
            : salaryChangeData.formData?.effectiveDate;
        const effectiveUnknownEndDate =
          syncDatesWithPosition &&
          positionChangeData?.unknownEndDate !== undefined
            ? positionChangeData.unknownEndDate
            : salaryChangeData.formData?.unknownEndDate;
        const effectiveEndDate =
          syncDatesWithPosition && positionChangeData?.endDate
            ? positionChangeData.endDate
            : salaryChangeData.formData?.endDate;

        const hasStartDate = !!effectiveStartDate;
        const hasEndDateIfRequired =
          effectiveUnknownEndDate || !!effectiveEndDate;

        // Validate dates for errors (reuse position change date validation)
        const salaryDateValidationErrors = validateDateRange(
          effectiveStartDate || '',
          effectiveUnknownEndDate ? undefined : effectiveEndDate || '',
        );
        const salaryHasNoDateErrors =
          !salaryDateValidationErrors.effectiveDateError &&
          !salaryDateValidationErrors.endDateError;

        if (salaryChangeData.formData?.updateAmountOnly) {
          // Quick Update mode: requires selected salary, value, dates
          const hasSelectedSalary =
            !!salaryChangeData.formData?.selectedSalaryId;
          return (
            hasSelectedSalary &&
            hasValue &&
            hasStartDate &&
            hasEndDateIfRequired &&
            salaryHasNoDateErrors
          );
        } else {
          // Advanced mode: requires value, dates, frequency (no salary selection)
          const hasFrequency = !!salaryChangeData.formData?.frequency;
          return (
            hasValue &&
            hasStartDate &&
            hasEndDateIfRequired &&
            hasFrequency &&
            salaryHasNoDateErrors
          );
        }
      case StepType.EMPLOYEE_DETAILS:
        return true;
      default:
        return true;
    }
  }, [
    currentStep,
    reason,
    isReasonMandatory,
    positionChangeData,
    salaryChangeData,
  ]);

  if (!selectedEmployeeId) {
    return (
      <Stack gap={0}>
        <div className="text-center py-5">
          <h5 className="mb-4">{t('selectEmployee')}</h5>
          <SelectSingle
            name="employee"
            placeholder={t('employee.placeholder')}
            options={employees.map((emp) => ({
              value: emp.Id,
              label: emp.Name,
            }))}
            value={selectedEmployeeId || ''}
            onChange={(value) => {
              if (value) {
                setSelectedEmployeeId(value);
                onEmployeeSelect(value);
              }
            }}
          />
        </div>
        <NavigationButtons
          onBack={onBack}
          disableNext={true}
          type="button"
          currentStep={currentSubStep + 1}
          totalSteps={availableSteps.length}
        />
      </Stack>
    );
  }

  if (recordFieldsLoading) {
    return (
      <Stack gap={0}>
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" />
        </div>
        <NavigationButtons
          onBack={handleSubStepBack}
          disableNext={true}
          type="button"
          currentStep={currentSubStep + 1}
          totalSteps={availableSteps.length}
        />
      </Stack>
    );
  }

  // Render current step content
  const renderStepContent = () => {
    if (!currentStep) return null;

    switch (currentStep.type) {
      case StepType.REASON_FILES:
        return (
          <ReasonFilesStep
            reason={reason}
            setReason={setReason}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            isReasonMandatory={isReasonMandatory}
          />
        );

      case StepType.POSITION_CHANGE:
        return (
          <PositionChangeStep
            positionChangeData={positionChangeData}
            setPositionChangeData={setPositionChangeData}
            selectedEmployeeId={selectedEmployeeId}
          />
        );

      case StepType.SALARY_CHANGE:
        return (
          <SalaryChangeStep
            currentSalary={currentSalary}
            salaryChangeData={salaryChangeData}
            setSalaryChangeData={setSalaryChangeData}
            selectedEmployeeId={selectedEmployeeId}
            salaryChangeRef={salaryChangeRef}
            positionChangeData={positionChangeData}
          />
        );

      case StepType.EMPLOYEE_DETAILS:
        return (
          <EmployeeDetailsStep
            recordFields={recordFields}
            defaultValues={defaultValues}
            onChange={setEmployeeDetailsData}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Stack gap={0}>
      <div className="mb-4">
        <h4>{currentStep?.title}</h4>
        <p className="text-muted">
          {t(`steps.${currentStep?.type}Description`)}
        </p>
      </div>

      {renderStepContent()}

      <NavigationButtons
        onBack={handleSubStepBack}
        onNext={handleSubStepNext}
        disableNext={!isCurrentStepValid}
        isLastStep={currentSubStep === availableSteps.length - 1}
        currentStep={currentSubStep + 1}
        totalSteps={availableSteps.length}
      />
    </Stack>
  );
};

const ReasonFilesStep: React.FC<{
  reason: string;
  setReason: (reason: string) => void;
  selectedFiles: File[];
  setSelectedFiles: (files: File[]) => void;
  isReasonMandatory: boolean;
}> = ({
  reason,
  setReason,
  selectedFiles,
  setSelectedFiles,
  isReasonMandatory,
}) => {
  const t = useNamespacedTranslation('changeRequests.requestForm');

  return (
    <Stack gap={3}>
      <div>
        <Form.Group>
          <Form.Label>
            {t('reason.label')}
            {isReasonMandatory && <span className="text-danger"> *</span>}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('reason.placeholder')}
            required={isReasonMandatory}
          />
        </Form.Group>
      </div>

      <div>
        <Form.Label>{t('attachments.label')}</Form.Label>
        <UploadFile
          multiple
          acceptExtensions=".jpg,.jpeg,.png,.pdf,.txt"
          onChange={(files: FileList) =>
            setSelectedFiles([...selectedFiles, ...Array.from(files)])
          }
        />
        <Attachments
          files={selectedFiles}
          onChange={(files: File[]) => setSelectedFiles(files)}
        />
      </div>
    </Stack>
  );
};

const PositionChangeStep: React.FC<{
  positionChangeData?: PositionChangeData;
  setPositionChangeData: (data: PositionChangeData | undefined) => void;
  selectedEmployeeId: string;
}> = ({ positionChangeData, setPositionChangeData, selectedEmployeeId }) => {
  return (
    <div>
      <PositionChangeComponent
        selectedEmployeeId={selectedEmployeeId}
        onChange={setPositionChangeData}
        value={positionChangeData}
      />
    </div>
  );
};

const SalaryChangeStep: React.FC<{
  currentSalary: number;
  salaryChangeData?: SalaryChangeData;
  setSalaryChangeData: (data: SalaryChangeData | undefined) => void;
  selectedEmployeeId: string;
  salaryChangeRef: React.RefObject<SalaryChangeStepsWrapperRef>;
  positionChangeData?: PositionChangeData;
}> = ({
  currentSalary,
  salaryChangeData,
  setSalaryChangeData,
  selectedEmployeeId,
  salaryChangeRef,
  positionChangeData,
}) => {
  const memoizedPositionData = useMemo(() => {
    return positionChangeData
      ? {
          effectiveDate: positionChangeData.effectiveDate,
          unknownEndDate: positionChangeData.unknownEndDate,
          endDate: positionChangeData.endDate,
        }
      : undefined;
  }, [positionChangeData]);

  return (
    <div>
      <SalaryChangeStepsWrapper
        ref={salaryChangeRef}
        selectedEmployeeId={selectedEmployeeId}
        currentSalary={currentSalary}
        currency="€"
        onChange={setSalaryChangeData}
        value={salaryChangeData}
        positionChangeData={memoizedPositionData}
      />
    </div>
  );
};

const EmployeeDetailsStep: React.FC<{
  recordFields: Array<RecordField>;
  defaultValues: { [key: string]: string };
  onChange: (fieldData: { [key: string]: string }) => void;
}> = ({ recordFields, defaultValues, onChange }) => {
  const t = useNamespacedTranslation('changeRequests.requestForm');
  const [fieldValues, setFieldValues] = useState<{ [key: string]: string }>(
    defaultValues,
  );

  React.useEffect(() => {
    setFieldValues(defaultValues);
  }, [defaultValues, recordFields]);

  const handleFieldChange = useCallback(
    (value: any, fieldInfo: any) => {
      const updatedValues = { ...fieldValues, [fieldInfo.name]: value };
      setFieldValues(updatedValues);

      const changedFields = getChangedFields(updatedValues, recordFields);
      onChange(changedFields);
    },
    [fieldValues, recordFields, onChange],
  );

  return (
    <CustomForm
      onSubmit={() => {}} // Form submission is handled by parent
      defaultValues={defaultValues}
      isChangeable={true}>
      <div className="fields-section">
        <Stack
          gap={3}
          className="fields-container"
          style={{
            maxHeight: '15rem',
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '0 0.25rem',
          }}>
          {recordFields.map((field) => (
            <div key={field.fieldInfo.name} className="mb-3">
              <FieldInput
                fieldInfo={{
                  ...field.fieldInfo,
                  onChange: handleFieldChange,
                }}
              />
              <div className="text-muted small d-flex gap-2 ms-1 mt-n3">
                <span>{t('current')}:</span>
                <span>
                  <FormatFieldValue
                    value={field.value}
                    type={field.fieldInfo.type}
                  />
                </span>
              </div>
            </div>
          ))}
        </Stack>
      </div>
    </CustomForm>
  );
};

// Helper function to get changed fields
const getChangedFields = (
  currentValues: NewFieldValues,
  originalFields: Array<RecordField>,
): NewFieldValues => {
  const changedFields: NewFieldValues = {};

  for (const fieldName of Object.keys(currentValues)) {
    const originalField = originalFields.find(
      (field) => field.fieldInfo.name === fieldName,
    );
    const originalValue = originalField?.value || '';
    const newValue = currentValues[fieldName];

    if (newValue.toString() !== originalValue.toString()) {
      changedFields[fieldName] = newValue;
    }
  }
  return changedFields;
};

type NavigationButtonsProps = {
  onBack: () => void;
  onNext?: () => void;
  disableNext?: boolean;
  isLastStep?: boolean;
  currentStep: number;
  totalSteps: number;
  type?: 'submit' | 'button';
};

const NavigationButtons: React.FC<NavigationButtonsProps> = ({
  onBack,
  onNext,
  disableNext = false,
  isLastStep = false,
  currentStep,
  totalSteps,
  type = 'button',
}) => {
  const t = useNamespacedTranslation('changeRequests.requestForm');

  const handleNext = () => {
    if (onNext) {
      onNext();
    }
  };

  return (
    <Row className="align-items-center mt-4">
      <Col>
        <Button
          variant="outline-primary"
          label={t('buttons.back')}
          onClick={onBack}
        />
      </Col>
      <Col className="text-center">
        <StepIndicator totalSteps={totalSteps} currentStep={currentStep} />
      </Col>
      <Col className="text-end">
        <Button
          type={type}
          variant="primary"
          label={isLastStep ? t('buttons.confirm') : t('buttons.next')}
          disabled={disableNext}
          onClick={type === 'button' ? handleNext : undefined}
        />
      </Col>
    </Row>
  );
};
